import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Settings, Plus, Pencil, Tag, AlertTriangle, Check, X } from 'lucide-react';
import api from '@/lib/api';

const ConfiguracoesVendas = () => {
  const [statusList, setStatusList] = useState([]);
  const [motivosList, setMotivosList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog Status
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [editandoStatus, setEditandoStatus] = useState(null);
  const [statusForm, setStatusForm] = useState({ nome: '', cor: '#3b82f6' });

  // Dialog Motivo
  const [motivoDialogOpen, setMotivoDialogOpen] = useState(false);
  const [editandoMotivo, setEditandoMotivo] = useState(null);
  const [motivoForm, setMotivoForm] = useState({ nome: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusRes, motivosRes] = await Promise.all([
        api.get('/config/status-venda'),
        api.get('/config/motivos-cancelamento'),
      ]);
      setStatusList(statusRes.data);
      setMotivosList(motivosRes.data);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  // === STATUS ===
  const handleNovoStatus = () => {
    setEditandoStatus(null);
    setStatusForm({ nome: '', cor: '#3b82f6' });
    setStatusDialogOpen(true);
  };

  const handleEditarStatus = (item) => {
    setEditandoStatus(item);
    setStatusForm({ nome: item.nome, cor: item.cor || '#3b82f6' });
    setStatusDialogOpen(true);
  };

  const handleSalvarStatus = async () => {
    if (!statusForm.nome.trim()) {
      toast.error('Informe o nome do status');
      return;
    }
    try {
      if (editandoStatus?.is_padrao) {
        // Editar override de status padrão
        await api.put(`/config/status-padrao/${encodeURIComponent(editandoStatus.nome_original)}`, { cor: statusForm.cor, nome: statusForm.nome });
        toast.success('Status atualizado!');
      } else if (editandoStatus) {
        await api.put(`/config/status-venda/${editandoStatus.id}`, statusForm);
        toast.success('Status atualizado!');
      } else {
        await api.post('/config/status-venda', statusForm);
        toast.success('Status criado!');
      }
      setStatusDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar status');
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      await api.put(`/config/status-venda/${item.id}`, { ativo: !item.ativo });
      toast.success(item.ativo ? 'Status inativado' : 'Status ativado');
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  // === STATUS PADRÃO (override) ===
  const handleEditarStatusPadrao = (item) => {
    setEditandoStatus({ ...item, is_padrao: true, nome_original: item.nome_original || item.nome });
    setStatusForm({ nome: item.nome, cor: item.cor || '#64748b' });
    setStatusDialogOpen(true);
  };

  const handleToggleStatusPadrao = async (item) => {
    const nomeOriginal = item.nome_original || item.nome;
    try {
      await api.put(`/config/status-padrao/${encodeURIComponent(nomeOriginal)}`, { ativo: item.ativo === false ? true : false });
      toast.success(item.ativo === false ? 'Status ativado' : 'Status inativado');
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  // === MOTIVOS ===
  const handleNovoMotivo = () => {
    setEditandoMotivo(null);
    setMotivoForm({ nome: '' });
    setMotivoDialogOpen(true);
  };

  const handleEditarMotivo = (item) => {
    setEditandoMotivo(item);
    setMotivoForm({ nome: item.nome });
    setMotivoDialogOpen(true);
  };

  const handleSalvarMotivo = async () => {
    if (!motivoForm.nome.trim()) {
      toast.error('Informe o nome do motivo');
      return;
    }
    try {
      if (editandoMotivo) {
        await api.put(`/config/motivos-cancelamento/${editandoMotivo.id}`, motivoForm);
        toast.success('Motivo atualizado!');
      } else {
        await api.post('/config/motivos-cancelamento', motivoForm);
        toast.success('Motivo criado!');
      }
      setMotivoDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar motivo');
    }
  };

  const handleToggleMotivo = async (item) => {
    try {
      await api.put(`/config/motivos-cancelamento/${item.id}`, { ativo: !item.ativo });
      toast.success(item.ativo ? 'Motivo inativado' : 'Motivo ativado');
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar motivo');
    }
  };

  const cores = [
    '#3b82f6', '#2563eb', '#1d4ed8', '#10b981', '#059669', '#047857',
    '#f59e0b', '#d97706', '#f97316', '#ef4444', '#dc2626', '#b91c1c',
    '#8b5cf6', '#7c3aed', '#6d28d9', '#ec4899', '#db2777', '#be185d',
    '#06b6d4', '#0891b2', '#0e7490', '#84cc16', '#65a30d', '#4d7c0f',
    '#14b8a6', '#0d9488', '#0f766e', '#f43f5e', '#e11d48', '#be123c',
    '#6366f1', '#4f46e5', '#4338ca', '#d946ef', '#c026d3', '#a21caf',
    '#64748b', '#475569', '#334155', '#a855f7', '#9333ea', '#7e22ce',
    '#0ea5e9', '#0284c7', '#0369a1', '#22c55e', '#16a34a', '#15803d',
    '#eab308', '#ca8a04', '#a16207', '#78716c', '#57534e', '#44403c',
    '#000000', '#991b1b', '#1e3a5f', '#7f1d1d', '#fbbf24', '#fb923c',
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Settings className="h-8 w-8 text-gray-700" />
          Configurações de Vendas
        </h1>
        <p className="text-gray-500 mt-1">Gerencie os status de venda e motivos de cancelamento</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* STATUS DE VENDA */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-red-600" />
              Status de Venda
            </CardTitle>
            <Button size="sm" onClick={handleNovoStatus} data-testid="btn-novo-status">
              <Plus className="h-4 w-4 mr-1" />
              Novo Status
            </Button>
          </CardHeader>
          <CardContent>
            {/* Status padrão do sistema */}
            <p className="text-xs text-gray-500 mb-3 font-medium">Status do sistema:</p>
            <div className="space-y-2 mb-4">
              {statusList.filter(s => s.padrao).map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50" data-testid={`status-padrao-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: s.cor || '#64748b' }} />
                    <span className={`font-medium ${s.ativo === false ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{s.nome}</span>
                    <Badge variant="outline" className="text-xs text-gray-400">Padrão</Badge>
                    {s.ativo === false && <Badge variant="outline" className="text-xs text-red-400">Inativo</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={s.ativo !== false} onCheckedChange={() => handleToggleStatusPadrao(s)} data-testid={`toggle-padrao-${s.id}`} />
                    <Button size="sm" variant="ghost" onClick={() => handleEditarStatusPadrao(s)} data-testid={`edit-padrao-${s.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Status customizados */}
            <p className="text-xs text-gray-500 mb-3 font-medium">Status personalizados:</p>
            <div className="space-y-2">
              {statusList.filter(s => !s.padrao).length === 0 && (
                <p className="text-sm text-gray-400 italic">Nenhum status personalizado criado</p>
              )}
              {statusList.filter(s => !s.padrao).map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50" data-testid={`status-item-${item.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.cor || '#3b82f6' }} />
                    <span className={`font-medium ${!item.ativo ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{item.nome}</span>
                    {!item.ativo && <Badge variant="outline" className="text-xs text-gray-400">Inativo</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={item.ativo} onCheckedChange={() => handleToggleStatus(item)} data-testid={`toggle-status-${item.id}`} />
                    <Button size="sm" variant="ghost" onClick={() => handleEditarStatus(item)} data-testid={`edit-status-${item.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* MOTIVOS DE CANCELAMENTO */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Motivos de Cancelamento
            </CardTitle>
            <Button size="sm" onClick={handleNovoMotivo} data-testid="btn-novo-motivo">
              <Plus className="h-4 w-4 mr-1" />
              Novo Motivo
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {motivosList.length === 0 && (
                <p className="text-sm text-gray-400 italic">Nenhum motivo cadastrado</p>
              )}
              {motivosList.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50" data-testid={`motivo-item-${item.id}`}>
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`h-4 w-4 ${item.ativo ? 'text-red-500' : 'text-gray-300'}`} />
                    <span className={`font-medium ${!item.ativo ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{item.nome}</span>
                    {!item.ativo && <Badge variant="outline" className="text-xs text-gray-400">Inativo</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={item.ativo} onCheckedChange={() => handleToggleMotivo(item)} data-testid={`toggle-motivo-${item.id}`} />
                    <Button size="sm" variant="ghost" onClick={() => handleEditarMotivo(item)} data-testid={`edit-motivo-${item.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialog Status */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editandoStatus ? 'Editar Status' : 'Novo Status de Venda'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Status *</Label>
              <Input
                value={statusForm.nome}
                onChange={(e) => setStatusForm({ ...statusForm, nome: e.target.value.toUpperCase() })}
                placeholder="Ex: EM ANALISE"
                className="uppercase"
                data-testid="input-status-nome"
              />
            </div>
            <div>
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {cores.map(cor => (
                  <button
                    key={cor}
                    type="button"
                    onClick={() => setStatusForm({ ...statusForm, cor })}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${statusForm.cor === cor ? 'border-gray-900 scale-125 ring-2 ring-offset-1 ring-gray-400' : 'border-transparent hover:scale-110'}`}
                    style={{ backgroundColor: cor }}
                  />
                ))}
                <input
                  type="color"
                  value={statusForm.cor}
                  onChange={(e) => setStatusForm({ ...statusForm, cor: e.target.value })}
                  className="w-7 h-7 rounded-full cursor-pointer border-2 border-gray-300"
                  title="Cor personalizada"
                />
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-sm text-gray-500">Preview:</span>
                <Badge variant="outline" className="text-xs border-0 px-3 py-1" style={{ backgroundColor: statusForm.cor, color: '#fff' }}>{statusForm.nome || 'STATUS'}</Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSalvarStatus} data-testid="btn-salvar-status">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Motivo */}
      <Dialog open={motivoDialogOpen} onOpenChange={setMotivoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editandoMotivo ? 'Editar Motivo' : 'Novo Motivo de Cancelamento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Motivo *</Label>
              <Input
                value={motivoForm.nome}
                onChange={(e) => setMotivoForm({ ...motivoForm, nome: e.target.value.toUpperCase() })}
                placeholder="Ex: DESISTENCIA DO CLIENTE"
                className="uppercase"
                data-testid="input-motivo-nome"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMotivoDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSalvarMotivo} data-testid="btn-salvar-motivo">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfiguracoesVendas;
